import React, { useState, useEffect } from 'react';
import { Book, Edit2, Save, X, Search, AlertTriangle, CheckCircle, ExternalLink, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLibraryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLibrary();
  }, []);

  const fetchLibrary = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/library/admin/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      } else {
        toast.error('Failed to fetch library items');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (item) => {
    setEditingId(item.id);
    setEditData({
      title: item.title || '',
      author: item.author || '',
      category: item.category || '',
      description: item.description || '',
      cover_url: item.cover_url || ''
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveItem = async (itemId) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/library/admin/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        toast.success('Library item updated!');
        setEditingId(null);
        fetchLibrary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Check if thumbnail URL might be wrong
  const checkThumbnailMatch = (item) => {
    if (!item.cover_url || !item.title) return true;
    
    // If it's an external URL (unsplash, etc.), skip check
    if (item.cover_url.startsWith('http')) return true;
    
    // Extract filename from cover_url
    const filename = item.cover_url.split('/').pop().replace('.jpg', '').replace(/_/g, ' ').toLowerCase();
    const titleLower = item.title.toLowerCase().replace(/_/g, ' ');
    
    // Check if title words appear in filename
    const titleWords = titleLower.split(' ').filter(w => w.length > 3);
    const matchingWords = titleWords.filter(word => filename.includes(word));
    
    return matchingWords.length >= 1;
  };

  const filteredItems = items.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.title?.toLowerCase().includes(search) ||
      item.author?.toLowerCase().includes(search) ||
      item.category?.toLowerCase().includes(search)
    );
  });

  // Count potential mismatches
  const mismatchCount = items.filter(item => !checkThumbnailMatch(item)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Library Management</h1>
          <p className="text-gray-600 mt-1">Review and fix ebook data (titles, thumbnails)</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border shadow-sm">
            <p className="text-sm text-gray-500">Total Ebooks</p>
            <p className="text-2xl font-bold text-gray-900">{items.length}</p>
          </div>
          <div className={`p-4 rounded-xl border shadow-sm ${mismatchCount > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
            <p className="text-sm text-yellow-700">Potential Mismatches</p>
            <p className="text-2xl font-bold text-yellow-900">{mismatchCount}</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white p-4 rounded-xl border shadow-sm mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, author, or category..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* Items List */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-200">
            {filteredItems.map((item) => {
              const isEditing = editingId === item.id;
              const hasMismatch = !checkThumbnailMatch(item);
              
              return (
                <div key={item.id} className={`p-6 ${hasMismatch ? 'bg-yellow-50' : ''}`}>
                  <div className="flex items-start gap-4">
                    {/* Thumbnail Preview */}
                    <div className="w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border">
                      {item.cover_url ? (
                        <img
                          src={item.cover_url.startsWith('http') ? item.cover_url : `${API_URL}${item.cover_url}`}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23ddd" width="100" height="100"/><text x="50" y="50" dominant-baseline="middle" text-anchor="middle" fill="%23999">No Image</text></svg>';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Book className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      {isEditing ? (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={editData.title}
                            onChange={(e) => setEditData({...editData, title: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg font-semibold"
                            placeholder="Title"
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={editData.author}
                              onChange={(e) => setEditData({...editData, author: e.target.value})}
                              className="px-3 py-2 border rounded-lg text-sm"
                              placeholder="Author"
                            />
                            <input
                              type="text"
                              value={editData.category}
                              onChange={(e) => setEditData({...editData, category: e.target.value})}
                              className="px-3 py-2 border rounded-lg text-sm"
                              placeholder="Category"
                            />
                          </div>
                          <input
                            type="text"
                            value={editData.cover_url}
                            onChange={(e) => setEditData({...editData, cover_url: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="Cover URL (e.g., /api/static/thumbnails/...)"
                          />
                          <textarea
                            value={editData.description}
                            onChange={(e) => setEditData({...editData, description: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="Description"
                            rows={2}
                          />
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{item.title}</h3>
                            {hasMismatch && (
                              <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded-full flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Check thumbnail
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{item.author}</p>
                          <p className="text-xs text-purple-600 mb-2">{item.category}</p>
                          <p className="text-sm text-gray-500 line-clamp-2">{item.description}</p>
                          <p className="text-xs text-gray-400 mt-2 truncate">
                            <span className="font-medium">Cover:</span> {item.cover_url || 'Not set'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveItem(item.id)}
                            disabled={saving}
                            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => startEditing(item)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="Edit item"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLibraryPage;
